/**
 * © 2025 Indikin.online. All rights reserved.
 * Made with ❤️ by YalaCoder.
 *
 * --- REWRITE SUMMARY (V3.3 - LAYOUT FIX: ADD BANNER STATE) ---
 * - MISSION: To fix the layout bug where the footer and scroll button
 * did not adjust after the cookie banner was dismissed.
 *
 * - THE BUG: The visibility of the cookie banner was managed internally
 * by the banner component itself. This created a state synchronization
 * issue where the parent App component didn't know whether the banner
 * was active on initial load, causing incorrect layout calculations.
 *
 * - THE FIX (Centralized State):
 * 1.  NEW STATE: A new state variable, `isBannerActive`, has been introduced.
 * 2.  STATE LOGIC: This state is derived directly from the `consent` value.
 * - An effect now sets `isBannerActive` to `true` if `consent` is `null`
 * (after a delay to prevent layout shift on load), and `false` otherwise.
 * 3.  NEW CONTEXT VALUE: `isBannerActive` is now exported as part of the
 * `AnalyticsContext.Provider` value.
 *
 * - RESULT: This change makes the `AnalyticsProvider` the single source of
 * truth for whether the cookie banner should be displayed. The main `App`
 * component can now consume this boolean to reliably control the rendering
 * of the banner and ensure the layout adjusts correctly in all scenarios.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import Cookies from 'js-cookie';
import { AnalyticsContext } from './analytics.context.js';
import { isTVDevice } from '../utils/tvDetect';

// --- Configuration ---
const GA_MEASUREMENT_ID = 'G-C9MN9V1DVK';
const CLARITY_ID = 'sfob75lp7i';
const COOKIE_NAME = 'indikin_cookie_consent';

// --- Analytics Provider Component ---
export const AnalyticsProvider = ({ children }) => {
  const [consent, setConsent] = useState(() => Cookies.get(COOKIE_NAME) || null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [nonce, setNonce] = useState(null);
  // --- NEW: State to control banner visibility ---
  const [isBannerActive, setIsBannerActive] = useState(false);

  const analyticsService = useRef(null);
  const beaconSentRef = useRef(false);

  useEffect(() => {
    const meta = document.getElementById('vite-nonce-meta');
    if (meta) {
      setNonce(meta.content);
    }
  }, []);

  // --- NEW: Effect to manage banner visibility based on consent ---
  useEffect(() => {
    if (consent === null) {
      // If no consent is stored, activate the banner after a short delay.
      const timer = setTimeout(() => {
        setIsBannerActive(true);
      }, 1500);
      return () => clearTimeout(timer);
    } else {
      // If consent is already given, ensure the banner is not active.
      setIsBannerActive(false);
    }
  }, [consent]);


  useEffect(() => {
    if (consent === 'accepted' && nonce && !isInitialized) {
      const timer = setTimeout(async () => {
        console.log('Analytics consent accepted. Initializing services with nonce after delay...');
        
        const analytics = await import('../services/analytics');
        analyticsService.current = analytics;
        
        window.dataLayer = window.dataLayer || [];
        function gtag() {
          window.dataLayer.push(arguments);
        }
        window.gtag = gtag;
        
        gtag('js', new Date());
        
        const configOptions = {};
        if (isTVDevice()) {
          configOptions.device_category = 'tv';
        }
        gtag('config', GA_MEASUREMENT_ID, configOptions);
        
        const gaScript = document.createElement('script');
        gaScript.async = true;
        gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
        gaScript.nonce = nonce;
        document.head.appendChild(gaScript);
        
        analytics.initMicrosoftClarity(CLARITY_ID, nonce);

        setIsInitialized(true);
        console.log('Analytics services initialized.');
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [consent, isInitialized, nonce]);

  useEffect(() => {
    if (!isInitialized) {
      return;
    }

    const sendSessionEndBeacon = (reason) => {
      if (!beaconSentRef.current && analyticsService.current) {
        analyticsService.current.sendBeacon({
          event: 'session_end',
          timestamp: new Date().toISOString(),
          reason: reason,
        });
        beaconSentRef.current = true;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        sendSessionEndBeacon('visibility_hidden');
      }
    };

    const handlePageHide = () => {
      sendSessionEndBeacon('page_hide');
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [isInitialized]);

  const handleSetConsent = useCallback((consentChoice) => {
    Cookies.set(COOKIE_NAME, consentChoice, {
      expires: 365,
      path: '/',
      sameSite: 'Lax',
      secure: true,
    });
    setConsent(consentChoice);
    // When consent is set, immediately deactivate the banner
    setIsBannerActive(false);
  }, []);
  
  const trackPageView = useCallback((path) => {
    if (consent === 'accepted' && isInitialized && analyticsService.current) {
      beaconSentRef.current = false; 
      analyticsService.current.trackPageView(path);
    }
  }, [consent, isInitialized]);

  const value = {
    consent,
    setConsent: handleSetConsent,
    trackPageView,
    isInitialized,
    // --- NEW: Expose banner active state ---
    isBannerActive,
  };

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};
